import { makeObservable, observable } from 'mobx';
import Konva from 'konva';
import { CrossSectionViewState } from './CrossSectionWindow';

// class responsible only for blank canvas stage with some common functions:
//  - mouse/touch controls, zoom, resize handing
// drawing specific things onto stage should be done outside of it
export class CrossSectionCanvas {
    onViewChanged: () => void;
    canvasWrapperEl: HTMLDivElement;

    stage: Konva.Stage;
    staticLayer: Konva.Layer; // not moving with the camera
    contentLayer: Konva.Layer;
    tooltipLayer: Konva.Layer; // clear on mouse-out
    private stickyTooltipLayer: Konva.Layer; // clears when camera moves
    private cursorLayer: Konva.Layer;
    private measureToolsLayer: Konva.Layer;
    measureTooltipLayer: Konva.Layer;

    @observable yScale = kuura.getCrossSectionSettings().yScale ?? 1; // local y scaling, doesnt require fetching data
    yScaleInitial = this.yScale;
    @observable widthMetersInitial = 0; // zoom level at moment when data was loaded from server
    widthMeters = 0; // current dynamic zoom value

    canvasOptions = {
        side_margin: 0,
        top_margin: 40,
        bottom_margin: 40,
        x_scale: 0,
        y_scale: 0,
    };

    oldXScale = 0.0; // scale on previous scene render, used for zoom-in/out to cursor correction
    oldYScale = 0.0;

    // grid
    yCenterLine = 0;
    xCenterLine = 0;
    lowestPoint = 0; // same as minY in old long-section code
    highestPoint = 30; // same as minY in old long-section code

    // view & events
    panX = 0;
    panY = 0;
    initialPanY = 0;
    mouseDown = false;
    pinchDistance = 0;
    mouseMoveCount = 0; // needed to skip redraw on first move event
    mouseX: number;
    mouseY: number;
    zoomToCursorCorrectionCallback: () => void; // outside code must do the correction since its not same for regular and long-section

    // tools
    @observable distanceToolActive = false;
    @observable areaToolActive = false;
    snapOn = false;
    snapX: number = null;
    snapY: number = null;
    distanceToolStartX = 0;
    distanceToolStartY = 0;
    distanceToolEndX = 0;
    distanceToolEndY = 0;
    areaToolBeginNew = false;
    areaToolPoint = [];

    private resizeObserver: ResizeObserver = null;

    constructor(opts: { zoomToCursorCorrection: () => void; view?: CrossSectionViewState }) {
        makeObservable(this);
        this.zoomToCursorCorrectionCallback = opts.zoomToCursorCorrection;
        this.restoreViewState(opts.view);

        //Konva.autoDrawEnabled = false;
    }

    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.removeEventListeners();
    }

    async init(opts: { canvasWrapperEl: HTMLDivElement; onViewChanged: () => void }) {
        console.log('csCanvas.init:', opts);
        this.canvasWrapperEl = opts.canvasWrapperEl;
        this.onViewChanged = opts.onViewChanged;

        try {
            this.resizeObserver = new ResizeObserver(
                _.throttle((entries, observer) => {
                    this.stage?.destroy(); // stage.width(123) flickers way worse than recreating whole thing
                    this.stage = null;
                    this.clearStage(entries[0].contentRect.width, entries[0].contentRect.height);
                    this.onViewChanged();
                }, 32)
            );
            this.resizeObserver.observe(this.canvasWrapperEl);
        } catch (e) {
            console.warn('ResizeObserver not supported', e);
        }

        this.addEventListeners();
    }

    clearStage = (newWidth?: number, newHeight?: number) => {
        if (!this.widthMeters || !this.canvasWrapperEl) {
            return;
        }

        const stageWidth = newWidth ?? this.width;
        const stageHeight = newHeight ?? this.height;

        if (!stageWidth) {
            return;
        }

        if (!this.stage) {
            this.stage = new Konva.Stage({
                clearBeforeDraw: false,
                container: this.canvasWrapperEl,
                width: stageWidth,
                height: stageHeight,
            });

            this.staticLayer = new Konva.Layer({
                clearBeforeDraw: false,
                listening: false,
            });
            this.stage.add(this.staticLayer);

            this.contentLayer = new Konva.Layer({
                clearBeforeDraw: true,
            });
            this.stage.add(this.contentLayer);

            const fastLayerOpts = {
                clearBeforeDraw: true,
                listening: false,
            };

            this.tooltipLayer = new Konva.Layer(fastLayerOpts);
            this.stage.add(this.tooltipLayer);

            this.stickyTooltipLayer = new Konva.Layer(fastLayerOpts);
            this.stage.add(this.stickyTooltipLayer);

            this.measureTooltipLayer = new Konva.Layer(fastLayerOpts);
            this.stage.add(this.measureTooltipLayer);

            this.measureToolsLayer = new Konva.Layer(fastLayerOpts);
            this.stage.add(this.measureToolsLayer);

            this.cursorLayer = new Konva.Layer();
            this.stage.add(this.cursorLayer);
        } else {
            this.contentLayer.destroyChildren().clear();
            this.staticLayer.destroyChildren().clear();
            this.tooltipLayer.destroyChildren().clear();
            this.stickyTooltipLayer.destroyChildren().clear();
            this.measureTooltipLayer.destroyChildren().clear();
            this.measureToolsLayer.destroyChildren().clear();

            if (newWidth) {
                this.stage.width(stageWidth);
                this.stage.height(stageHeight);
            }
        }

        const sideMargin = stageWidth > 900 ? 168 : 80;

        this.canvasOptions = {
            top_margin: 40,
            bottom_margin: 40,
            side_margin: sideMargin,
            x_scale: (stageWidth - sideMargin) / this.widthMeters,
            y_scale: ((stageWidth - sideMargin) * this.yScale) / this.widthMeters,
        };

        if (this.oldXScale === 0) {
            this.oldXScale = this.canvasOptions.x_scale;
            this.oldYScale = this.canvasOptions.y_scale;
        }
    };

    clearTooltips() {
        this.tooltipLayer.destroyChildren().clear();
        this.stickyTooltipLayer.destroyChildren().clear();
    }

    addEventListeners = () => {
        this.canvasWrapperEl.addEventListener('mousemove', this.eventHandlers.mouseMove, false);
        this.canvasWrapperEl.addEventListener('mousedown', this.eventHandlers.mouseDown, false);
        this.canvasWrapperEl.addEventListener('mouseleave', this.eventHandlers.mouseUp, false);
        this.canvasWrapperEl.addEventListener('mouseup', this.eventHandlers.mouseUp, false);
        this.canvasWrapperEl.addEventListener('wheel', this.eventHandlers.mousewheel, false);
        this.canvasWrapperEl.addEventListener('dblclick', this.eventHandlers.dblclick, false);

        this.canvasWrapperEl.addEventListener('touchend', this.eventHandlers.mouseUp);
        this.canvasWrapperEl.addEventListener('touchcancel', this.eventHandlers.mouseUp);
        this.canvasWrapperEl.addEventListener('touchstart', this.eventHandlers.mouseDown);
        this.canvasWrapperEl.addEventListener('touchmove', this.eventHandlers.mouseMove);

        this.canvasWrapperEl.addEventListener('keydown', this.eventHandlers.keydown);
    };

    removeEventListeners = () => {
        this.canvasWrapperEl.removeEventListener('mousemove', this.eventHandlers.mouseMove, false);
        this.canvasWrapperEl.removeEventListener('mousedown', this.eventHandlers.mouseDown, false);
        this.canvasWrapperEl.removeEventListener('mouseleave', this.eventHandlers.mouseUp, false);
        this.canvasWrapperEl.removeEventListener('mouseup', this.eventHandlers.mouseUp, false);
        this.canvasWrapperEl.removeEventListener('wheel', this.eventHandlers.mousewheel, false);
        this.canvasWrapperEl.removeEventListener('dblclick', this.eventHandlers.dblclick, false);

        this.canvasWrapperEl.removeEventListener('touchend', this.eventHandlers.mouseUp);
        this.canvasWrapperEl.removeEventListener('touchcancel', this.eventHandlers.mouseUp);
        this.canvasWrapperEl.removeEventListener('touchstart', this.eventHandlers.mouseDown);
        this.canvasWrapperEl.removeEventListener('touchmove', this.eventHandlers.mouseMove);

        this.canvasWrapperEl.removeEventListener('keydown', this.eventHandlers.keydown);
    };

    // applyLayerTransform() {
    //     this.contentLayer.offsetX(this.panX * -1);
    //     this.contentLayer.offsetY(this.panY * -1);
    //     this.tooltipLayer.offsetX(this.panX * -1);
    //     this.tooltipLayer.offsetY(this.panY * -1);
    // }

    eventHandlers = {
        mouseDown: e => {
            e.preventDefault();
            e.stopPropagation();

            if (this.mouseDown) {
                return;
            }
            const isTouch = !!e.touches && !!e.touches.length;
            this.mouseDown = true;

            if (isTouch || e.which === 1) {
                this.mouseMoveCount = 0;

                if (this.distanceToolActive) {
                    if (this.distanceToolEndX || !this.distanceToolStartX) {
                        // set start
                        this.distanceToolEndX = null;
                        this.distanceToolEndY = null;
                        this.distanceToolStartX = this.screenCoordsToMetersX(this.snapOn ? this.snapX : this.mouseX);
                        this.distanceToolStartY = this.screenCoordsToMetersY(this.snapOn ? this.snapY : this.mouseY);
                    } else {
                        // set end
                        this.distanceToolEndX = this.screenCoordsToMetersX(this.snapOn ? this.snapX : this.mouseX);
                        this.distanceToolEndY = this.screenCoordsToMetersY(this.snapOn ? this.snapY : this.mouseY);
                    }
                    this.onViewChanged();
                }
                if (this.areaToolActive) {
                    const point = { x: this.screenCoordsToMetersX(this.snapOn ? this.snapX : this.mouseX), y: this.screenCoordsToMetersY(this.snapOn ? this.snapY : this.mouseY) };
                    if (
                        this.areaToolBeginNew === false &&
                        this.areaToolPoint.length > 1 &&
                        ((this.areaToolPoint[this.areaToolPoint.length - 1].x === this.screenCoordsToMetersX(this.snapX) &&
                            this.areaToolPoint[this.areaToolPoint.length - 1].y === this.screenCoordsToMetersY(this.snapY)) ||
                            (this.areaToolPoint[this.areaToolPoint.length - 1].x === this.screenCoordsToMetersX(this.mouseX) &&
                                this.areaToolPoint[this.areaToolPoint.length - 1].y === this.screenCoordsToMetersY(this.mouseY)))
                    ) {
                        this.areaToolBeginNew = true;
                    } else {
                        if (this.areaToolBeginNew) {
                            this.areaToolBeginNew = false;
                            this.areaToolPoint = [];
                        }
                        this.areaToolPoint.push(point);
                    }
                    this.onViewChanged();
                }
            }
        },

        mouseUp: e => {
            e.preventDefault();
            e.stopPropagation();
            this.mouseDown = false;
            this.mouseMoveCount = 0;
            this.pinchDistance = 0;
        },

        mouseMove: _.throttle(e => {
            const rect = this.canvasWrapperEl.querySelector('canvas')?.getBoundingClientRect();
            if (!rect) {
                console.log('mouseMove error - no canvas');
                return;
            }
            let clientX = e.clientX;
            let clientY = e.clientY;

            if (e.touches && e.touches[0]) {
                clientX = Math.round(e.touches[0].clientX);
                clientY = Math.round(e.touches[0].clientY);

                if (e.touches.length >= 2) {
                    const p1 = e.touches[0];
                    const p2 = e.touches[1];
                    const a = p1.clientX - p2.clientX;
                    const b = p1.clientY - p2.clientY;
                    const c = Math.sqrt(a * a + b * b);

                    if (this.mouseMoveCount > 1 && this.pinchDistance > 20) {
                        if (this.pinchDistance > c) {
                            this.zoomOut();
                        } else {
                            this.zoomIn();
                        }
                    }
                    this.pinchDistance = c;
                }
            }

            if (this.mouseX && this.mouseY && this.mouseDown) {
                if (this.mouseMoveCount > 1) {
                    // here we are draggin and utilizing max limits for panning
                    this.panX += clientX - rect.left - this.mouseX;
                    this.panY += clientY - rect.top - this.mouseY;
                    // this.applyLayerTransform();
                    this.onViewChanged();
                }
            }

            this.mouseMoveCount += 1;
            this.mouseX = clientX - rect.left;
            this.mouseY = clientY - rect.top;

            if (this.mouseDown === false) {
                this.drawDistanceTool();
                if (!this.areaToolBeginNew) {
                    this.drawAreaTool();
                }
                this.drawSnapCursor(false, this.mouseX, this.mouseY);
            }
        }, 16),

        mousewheel: e => {
            e.preventDefault();
            const delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
            if (delta < 0) {
                this.zoomOut(true);
            } else {
                this.zoomIn(true);
            }
        },

        dblclick: e => {
            if (e.button === 0) {
                this.clearTooltips();
            }
        },

        keydown: evt => {
            const key = evt.keyCode || evt.which;
            switch (key) {
                case 27: // ESC
                    this.clearAllTools();
                    break;
                case 39: // right
                    this.panX += 10;
                    this.onViewChanged();
                    break;
                case 37: // left
                    this.panX -= 10;
                    this.onViewChanged();
                    break;
                case 38: // up
                    this.panY -= 10;
                    this.onViewChanged();
                    break;
                case 40: // down
                    this.panY += 10;
                    this.onViewChanged();
                    break;
                case 107: // plus
                case 187: // equals == plus
                    this.zoomIn();
                    break;
                case 109: // subtract
                case 189: // dast minus
                    this.zoomOut();
                    break;
            }
        },
    };

    getCenter(): number {
        return this.width / 2 + this.panX;
    }

    screenCoordsToMetersX(x) {
        return (x - this.width / 2 - this.panX) / this.canvasOptions.x_scale;
    }

    screenCoordsToMetersY(y) {
        return (this.panY - y + (this.height - this.canvasOptions.bottom_margin) + this.lowestPoint * this.canvasOptions.y_scale) / this.canvasOptions.y_scale;
    }

    metersToScreenCoordsX(metersX) {
        return this.getCenter() + metersX * this.canvasOptions.x_scale;
    }

    metersToScreenCoordsY(metersY) {
        return this.height - this.canvasOptions.bottom_margin + this.lowestPoint * this.canvasOptions.y_scale - metersY * this.canvasOptions.y_scale + this.panY;
    }

    zoomIn = (zoomToCursor = false) => {
        this.widthMeters = utils.clamp(1, this.widthMeters * 0.85, 1000);

        this.oldXScale = this.canvasOptions.x_scale;
        this.oldYScale = this.canvasOptions.y_scale;
        this.clearStage();
        if (zoomToCursor) {
            this.zoomToCursorCorrection();
        }
        this.onViewChanged();
    };

    zoomOut = (zoomToCursor = false) => {
        this.widthMeters = utils.clamp(1, this.widthMeters * 1.15, this.widthMetersInitial * 5);

        this.oldXScale = this.canvasOptions.x_scale;
        this.oldYScale = this.canvasOptions.y_scale;
        this.clearStage();
        if (zoomToCursor) {
            this.zoomToCursorCorrection();
        }
        this.onViewChanged();
    };

    zoomReset = () => {
        console.log('zoomReset');
        this.widthMeters = this.widthMetersInitial;
        this.panX = 0;
        this.panY = this.initialPanY;
        this.distanceToolStartX = 0;
        this.distanceToolStartY = 0;
        this.clearStage();
        this.onViewChanged();
    };

    zoomToCursorCorrection = () => {
        const canvasOptions = this.canvasOptions;
        if (
            this.mouseX > canvasOptions.side_margin / 2 &&
            this.mouseX < this.width - canvasOptions.side_margin / 2 &&
            this.mouseY > canvasOptions.top_margin &&
            this.mouseY < this.height - canvasOptions.bottom_margin
        ) {
            this.zoomToCursorCorrectionCallback();
        }
    };

    drawGrid() {
        this.staticLayer.findOne('#grid')?.destroy();
        const group = new Konva.Group({
            id: 'grid',
        });

        const yGridlinesOffset = 100; // draw this many extra lines on top and bottom of the grid for drag feature
        const halfWidth = (this.width - this.canvasOptions.side_margin) / 2;
        const center = this.getCenter();
        const x1 = center - halfWidth;
        const x2 = center + halfWidth;
        const lineStroke = 'gray';
        const textColor = 'black';
        let previousY = 10000;

        //horizontal lines
        for (
            let y = this.height - this.canvasOptions.bottom_margin + this.canvasOptions.y_scale * yGridlinesOffset, z = this.lowestPoint - yGridlinesOffset;
            y >= this.canvasOptions.top_margin - this.canvasOptions.y_scale * yGridlinesOffset;
            y -= this.canvasOptions.y_scale, z++
        ) {
            if (previousY - y < 12) {
                continue;
            }
            if (y + this.panY > this.height - this.canvasOptions.bottom_margin + 1 || y + this.panY < this.canvasOptions.top_margin) {
                continue;
            }
            previousY = y;
            if (Math.abs(y + this.panY - this.height / 2) <= this.canvasOptions.y_scale / 2) {
                this.yCenterLine = z;
            }
            const line = new Konva.Line({
                points: [x1 - 10 - this.panX, y + this.panY, x2 - this.panX, y + this.panY],
                stroke: lineStroke,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });
            if (this.canvasOptions.y_scale >= 20 && y - 4 * (this.canvasOptions.y_scale / 5.0) + this.panY > this.canvasOptions.top_margin) {
                for (let i = 1; i < 5; i++) {
                    const y2 = y - i * (this.canvasOptions.y_scale / 5.0);

                    const subline = new Konva.Line({
                        points: [x1 - 10.0 - this.panX, y2 + this.panY, x1 - this.panX, y2 + this.panY],
                        stroke: lineStroke,
                        strokeWidth: 1,
                        lineCap: 'round',
                        lineJoin: 'round',
                        listening: false,
                    });
                    group.add(subline);
                }
            }

            const leftText = new Konva.Text({
                x: x1 - 15 - this.panX,
                y: y + this.panY,
                text: z + '',
                fontSize: 20,
                fontFamily: 'Calibri',
                fill: textColor,
                listening: false,
            });
            const rightText = new Konva.Text({
                x: x2 + 5 - this.panX,
                y: y + this.panY,
                text: z + '',
                fontSize: 20,
                fontFamily: 'Calibri',
                fill: textColor,
                listening: false,
            });
            leftText.offsetX(leftText.width());
            leftText.offsetY(leftText.height() / 2);
            rightText.offsetY(rightText.height() / 2);

            group.add(line);
            group.add(leftText);
            group.add(rightText);
        }
        // Vertical lines
        for (let i = 0, x = this.canvasOptions.x_scale * 10; i <= 100; x += this.canvasOptions.x_scale * 10) {
            i += 10;
            let leftX = center - x;
            if (leftX < this.canvasOptions.side_margin / 2 + 1) {
                leftX = this.canvasOptions.side_margin / 2 + 1;
            }
            let rightX = center + x;
            if (rightX > this.width - (this.canvasOptions.side_margin / 2 + 1)) {
                rightX = this.width - (this.canvasOptions.side_margin / 2 + 1);
            }
            const right = new Konva.Line({
                points: [rightX, this.height - this.canvasOptions.bottom_margin, rightX, this.canvasOptions.top_margin],
                stroke: lineStroke,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });
            group.add(right);
            const left = new Konva.Line({
                points: [leftX, this.height - this.canvasOptions.bottom_margin, leftX, this.canvasOptions.top_margin],
                stroke: lineStroke,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });
            group.add(left);

            if (center - x >= this.canvasOptions.side_margin / 2) {
                const leftText = new Konva.Text({
                    x: center - x,
                    y: this.height - this.canvasOptions.bottom_margin + 10,
                    text: -i + '',
                    fontSize: 20,
                    fontFamily: 'Calibri',
                    fill: textColor,
                    listening: false,
                });
                leftText.offsetX(leftText.width() / 2);
                group.add(leftText);
            }
            if (center + x <= this.width - this.canvasOptions.side_margin / 2) {
                const rightText = new Konva.Text({
                    x: center + x,
                    y: this.height - this.canvasOptions.bottom_margin + 10,
                    text: i + '',
                    fontSize: 20,
                    fontFamily: 'Calibri',
                    fill: textColor,
                    listening: false,
                });
                rightText.offsetX(rightText.width() / 2);
                group.add(rightText);
            }
        }

        const centerLine = new Konva.Line({
            points: [center, this.height - this.canvasOptions.bottom_margin, center, this.canvasOptions.top_margin],
            stroke: lineStroke,
            strokeWidth: 1,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
        });
        const centerText = new Konva.Text({
            x: center,
            y: this.height - this.canvasOptions.bottom_margin + 10,
            text: '0',
            fontSize: 20,
            fontFamily: 'Calibri',
            fill: textColor,
            listening: false,
        });

        centerText.offsetX(centerText.width() / 2);

        group.add(centerLine);
        group.add(centerText);

        this.staticLayer.add(group);
    }

    activateDistanceTool = () => {
        this.clearAreaTool();
        this.clearDistanceTool();
        this.distanceToolActive = true;
        this.onViewChanged();
    };

    activateAreaTool = () => {
        this.clearAreaTool();
        this.clearDistanceTool();
        this.areaToolActive = true;
        this.areaToolBeginNew = true;
        this.onViewChanged();
    };

    drawDistanceTool() {
        if (!this.distanceToolActive || (this.distanceToolStartX === 0 && this.distanceToolStartY === 0)) {
            return;
        }

        this.measureToolsLayer.destroyChildren();

        let endX = this.screenCoordsToMetersX(this.snapOn ? this.snapX : this.mouseX);
        let endY = this.screenCoordsToMetersY(this.snapOn ? this.snapY : this.mouseY);

        if (this.distanceToolEndX) {
            endX = this.distanceToolEndX;
            endY = this.distanceToolEndY;
        }

        const xMeters = Math.abs(this.metersToScreenCoordsX(endX) - this.metersToScreenCoordsX(this.distanceToolStartX)) / this.canvasOptions.x_scale;
        const yMeters = Math.abs(this.metersToScreenCoordsY(endY) - this.metersToScreenCoordsY(this.distanceToolStartY)) / this.canvasOptions.y_scale;
        const length = Math.sqrt(xMeters * xMeters + yMeters * yMeters);
        const unit = kuura.getUnit().abbr;

        let xOffset = 25;
        if (endX - this.distanceToolStartX < 0) {
            xOffset = -110;
        }

        const line = new Konva.Line({
            visible: true,
            name: 'test',
            points: [
                this.metersToScreenCoordsX(this.distanceToolStartX),
                this.metersToScreenCoordsY(this.distanceToolStartY),
                this.metersToScreenCoordsX(endX),
                this.metersToScreenCoordsY(endY),
            ],
            stroke: getRgb('0,0,0'),
            strokeWidth: 2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
        });
        line.dash([3, 4]);
        const textLine = 'Length: ' + length.toFixed(2) + ' ' + unit + '\nWidth: ' + xMeters.toFixed(2) + ' ' + unit + '\nHeight: ' + yMeters.toFixed(2) + ' ' + unit;
        const text = new Konva.Text({
            name: 'measure',
            x: this.metersToScreenCoordsX(endX) + xOffset,
            y: this.metersToScreenCoordsY(endY),
            text: textLine,
            fontSize: 14,
            fontFamily: 'Calibri',
            fill: 'black',
            listening: false,
        });

        const boxw = text.width() + 10;
        const boxh = text.height();
        const box = new Konva.Rect({
            x: this.metersToScreenCoordsX(endX) + xOffset,
            y: this.metersToScreenCoordsY(endY),
            width: boxw,
            height: boxh,
            fill: 'rgba(255,255,255,128)',
            opacity: 0.9,
            listening: false,
        });

        this.measureToolsLayer.add(box);
        this.measureToolsLayer.add(line);
        this.measureToolsLayer.add(text);
        this.measureToolsLayer.draw();
    }

    drawAreaTool = () => {
        if (this.areaToolPoint.length < 1) {
            return;
        }
        this.measureToolsLayer.destroyChildren();

        const currentMouse = { x: this.screenCoordsToMetersX(this.snapOn ? this.snapX : this.mouseX), y: this.screenCoordsToMetersY(this.snapOn ? this.snapY : this.mouseY) };
        const startPoint = { x: this.areaToolPoint[0].x, y: this.areaToolPoint[0].y };
        if (this.areaToolActive && !this.areaToolBeginNew) {
            this.areaToolPoint.push(currentMouse);
        }
        this.areaToolPoint.push(startPoint);

        let maxX = -99999;
        let maxY = -99999;
        let minX = 99999;
        let minY = 99999;

        for (let i = 0; i < this.areaToolPoint.length - 1; i++) {
            maxX = this.areaToolPoint[i + 1].x > maxX ? this.areaToolPoint[i + 1].x : maxX;
            maxY = this.areaToolPoint[i + 1].y > maxY ? this.areaToolPoint[i + 1].y : maxY;
            minX = this.areaToolPoint[i + 1].x < minX ? this.areaToolPoint[i + 1].x : minX;
            minY = this.areaToolPoint[i + 1].y < minY ? this.areaToolPoint[i + 1].y : minY;

            const line = new Konva.Line({
                visible: true,
                name: 'test',
                points: [
                    this.metersToScreenCoordsX(this.areaToolPoint[i].x),
                    this.metersToScreenCoordsY(this.areaToolPoint[i].y),
                    this.metersToScreenCoordsX(this.areaToolPoint[i + 1].x),
                    this.metersToScreenCoordsY(this.areaToolPoint[i + 1].y),
                ],
                stroke: getRgb('0,0,0'),
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });
            line.dash([3, 4]);
            this.measureToolsLayer.add(line);
        }
        this.areaToolPoint.pop();
        const area = this.calcPolygonArea();
        if (this.areaToolActive && !this.areaToolBeginNew) {
            this.areaToolPoint.pop();
        }

        const yOffset = -6;
        const xOffset = -50;
        const textLine = 'Area: ' + area.toFixed(2) + ' ' + kuura.getUnit().abbr + '2';

        const text = new Konva.Text({
            name: 'measure',
            x: this.metersToScreenCoordsX((maxX + minX) / 2) + xOffset,
            y: this.metersToScreenCoordsY((maxY + minY) / 2) + yOffset,
            text: textLine,
            fontSize: 14,
            fontFamily: 'Calibri',
            fill: 'black',
            listening: false,
        });

        const boxw = text.width() + 10;
        const boxh = text.height();
        const box = new Konva.Rect({
            x: this.metersToScreenCoordsX((maxX + minX) / 2) + xOffset,
            y: this.metersToScreenCoordsY((maxY + minY) / 2) + yOffset,
            width: boxw,
            height: boxh,
            fill: 'rgba(255,255,255,128)',
            opacity: 0.9,
            listening: false,
        });
        this.measureToolsLayer.add(box);
        this.measureToolsLayer.add(text);
        this.measureToolsLayer.draw();
    };

    calcPolygonArea = () => {
        let total = 0;
        for (let i = 0, l = this.areaToolPoint.length; i < l; i++) {
            const addX = this.areaToolPoint[i].x;
            const addY = this.areaToolPoint[i === this.areaToolPoint.length - 1 ? 0 : i + 1].y;
            const subX = this.areaToolPoint[i === this.areaToolPoint.length - 1 ? 0 : i + 1].x;
            const subY = this.areaToolPoint[i].y;
            total += addX * addY * 0.5;
            total -= subX * subY * 0.5;
        }
        return Math.abs(total);
    };

    drawSnapCursor = _.throttle((startSnap: boolean, x: number, y: number) => {
        this.cursorLayer.destroyChildren();
        if (this.areaToolActive || this.distanceToolActive) {
            if (startSnap) {
                this.snapOn = true;
                this.snapX = x;
                this.snapY = y;
            } else if (this.snapOn) {
                const distance = Math.abs(this.snapX - x) + Math.abs(this.snapY - y);
                if (distance > 15) {
                    this.snapOn = false;
                }
            }
            if (this.snapOn) {
                const snapCursor = new Konva.Circle({
                    visible: true,
                    name: 'test',
                    x: this.snapOn ? this.snapX : x,
                    y: this.snapOn ? this.snapY : y,
                    stroke: 'red',
                    radius: 4,
                });
                this.cursorLayer.add(snapCursor);
            }
        } else {
            this.snapOn = false;
        }
        this.cursorLayer.draw();
    }, 8);

    clearAllTools = () => {
        this.clearTooltips();
        this.clearAreaTool();
        this.clearDistanceTool();
        if (this.measureToolsLayer) {
            this.measureToolsLayer.destroyChildren();
            this.measureToolsLayer.draw();
        }
        this.onViewChanged();
    };

    clearAreaTool = () => {
        this.areaToolPoint = [];
        this.areaToolActive = false;
        this.areaToolBeginNew = false;
        if (this.measureToolsLayer) {
            this.measureToolsLayer.destroyChildren();
        }
    };

    clearDistanceTool = () => {
        this.distanceToolActive = false;
        this.distanceToolStartX = 0;
        this.distanceToolStartY = 0;
        this.distanceToolEndX = 0;
        this.distanceToolEndY = 0;
        if (this.measureToolsLayer) {
            this.measureToolsLayer.destroyChildren();
        }
    };

    drawTooltipAtMousePosition = (text: string, yOffset = 0, sticky = false) => {
        this.drawTooltip(this.mouseX, this.mouseY + yOffset, text, sticky);
    };

    drawTooltip = (x: number, y: number, text: string, sticky?: boolean) => {
        const textEl = new Konva.Text({
            x,
            y,
            text,
            fontSize: 12,
            fontFamily: 'Calibri',
            fill: 'black',
            listening: false,
        });
        textEl.offsetX(textEl.width() / 2);
        textEl.offsetY(textEl.height());

        const boxw = textEl.width() + 10;
        const boxh = textEl.height() + 10;

        const box = new Konva.Rect({
            x: x - boxw / 2,
            y: y - textEl.offsetY() - 5,
            width: boxw,
            height: boxh,
            fill: 'rgba(255,255,255,0.7)',
            opacity: 0.7,
            listening: false,
            cornerRadius: 4,
        });

        const layer = sticky ? this.stickyTooltipLayer : this.tooltipLayer;
        if (sticky) {
            this.clearHoverTooltips();
        }
        layer.add(box);
        layer.add(textEl);
        layer.draw();
    };

    drawHeader = (mainTitle: string, subTitle?: string) => {
        if (!this.staticLayer) {
            return;
        }
        this.staticLayer.findOne('#header')?.destroy();

        const group = new Konva.Group({
            id: 'header',
        });
        const mainText = new Konva.Text({
            y: 2,
            width: this.width,
            text: mainTitle,
            fontSize: 20,
            fontFamily: 'Calibri',
            fontStyle: 'bold',
            fill: 'red',
            align: 'center',
            listening: false,
        });
        group.add(mainText);

        if (subTitle) {
            const subtitleText = new Konva.Text({
                y: 22,
                width: this.width,
                text: subTitle,
                fontSize: 20,
                fontFamily: 'Calibri',
                fontStyle: 'bold',
                fill: 'red',
                align: 'center',
                listening: false,
            });
            group.add(subtitleText);
        }
        this.staticLayer.add(group);
    };

    clearHoverTooltips = () => {
        this.tooltipLayer.destroyChildren();
        this.tooltipLayer.draw();
    };

    yScaleIncrease = () => {
        this.yScale += 1;
        this.onViewChanged();
    };

    yScaleDecrease = () => {
        this.yScale = Math.max(1, this.yScale - 1);
        this.onViewChanged();
    };

    drawDebug = () => {
        const layer = this.contentLayer;
        const canvasOptions = this.canvasOptions;

        const text =
            ' y_scale:' +
            canvasOptions.y_scale.toFixed(2) +
            ' x_scale:' +
            canvasOptions.x_scale.toFixed(2) +
            ' ycenterline: ' +
            this.yCenterLine +
            ' lowestPoint:' +
            this.lowestPoint +
            ' origSecWidth:' +
            this.widthMetersInitial +
            ' panX:' +
            this.panX.toFixed(2) +
            ' panY:' +
            this.panY.toFixed(2) +
            '  initial panY:' +
            this.initialPanY.toFixed(2) +
            '  mouseX:' +
            this.mouseX +
            '  mouseY:' +
            this.mouseY;

        const debugText = new Konva.Text({
            x: 85,
            y: 40,
            text: text,
            fontSize: 20,
            fontFamily: 'Calibri',
            fill: 'black',
            listening: false,
        });
        layer.add(debugText);
    };

    get width() {
        if (this.stage) {
            return this.stage.width();
        } else {
            return this.canvasWrapperEl ? Math.round(this.canvasWrapperEl.getBoundingClientRect().width) : 0;
        }
    }

    get height() {
        if (this.stage) {
            return this.stage.height();
        } else {
            return this.canvasWrapperEl ? Math.round(this.canvasWrapperEl.getBoundingClientRect().height) : 0;
        }
    }

    getViewState(): CrossSectionViewState {
        return {
            panX: this.panX,
            panY: this.panY,
            initialPanY: this.initialPanY,
            yScale: this.yScale,
            yScaleInitial: this.yScaleInitial,
            widthMeters: Math.round(this.widthMeters),
            widthMetersInitial: Math.round(this.widthMetersInitial),
        };
    }

    restoreViewState(view: CrossSectionViewState) {
        if (!view) {
            return;
        }

        if (view.panX) {
            this.panX = view.panX;
        }
        if (view.panY) {
            this.panY = view.panY;
        }
        if (view.initialPanY) {
            this.initialPanY = view.initialPanY;
        }
        if (view.yScale && view.yScale > 0.2) {
            this.yScale = view.yScale;
        }
        if (view.yScaleInitial && view.yScaleInitial > 0.2) {
            this.yScaleInitial = view.yScaleInitial;
        }
        if (view.widthMetersInitial && !isNaN(view.widthMetersInitial) && view.widthMetersInitial > 0) {
            this.widthMetersInitial = view.widthMetersInitial;
        }
        if (view.widthMeters && !isNaN(view.widthMeters) && view.widthMeters > 0) {
            this.widthMeters = view.widthMeters;
        }
    }
}

const getRgb = (color: string) => 'rgb(' + color + ')';
